/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Image as ImageIcon, LoaderCircle, Tag, Video, X } from "lucide-react";
import { toast } from "sonner";

import { ExternalLinkPreviewCard } from "@/components/KindredSharedContent";
import KindredShareCard from "@/components/KindredShareCard";
import MentionComposerField from "@/components/MentionComposerField";
import PhotoUploadButton from "@/components/PhotoUploadButton";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { fetchExternalLinkPreview } from "@/lib/externalLinkPreview";
import { composeKindredContent, type KindredSharePayload } from "@/lib/kindredShare";
import { dispatchMentionNotifications, searchAllMentionSuggestions } from "@/lib/mentions";
import {
  getPrimaryExternalUrl,
  KINFEED_MAX_TOPIC_TAGS,
  KINFEED_POST_MAX_LENGTH,
  KINFEED_TOPIC_TAGS,
  isKinfeedPostWithinLimit,
  type ExternalLinkPreview,
  type KinfeedTopicTagId,
  type MentionDraft,
} from "../../shared/community";
import { AppButton, AppButtonRow, AppCard, AppCardBody, AppFieldGroup, AppText } from "../../ui-foundation";

const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
const ALLOWED_VIDEO_TYPES = new Set(["video/mp4", "video/quicktime", "video/webm"]);

const ComposePost = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const prefill = (location.state as any)?.prefill || "";
  const sharePayload = ((location.state as any)?.sharePayload ?? null) as KindredSharePayload | null;
  const [content, setContent] = useState(prefill);
  const [mentions, setMentions] = useState<MentionDraft[]>([]);
  const [photos, setPhotos] = useState<string[]>([]);
  const [videoPath, setVideoPath] = useState<string | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [topicTags, setTopicTags] = useState<KinfeedTopicTagId[]>([]);
  const [photoAreaOpen, setPhotoAreaOpen] = useState(false);
  const [topicsOpen, setTopicsOpen] = useState(false);
  const [linkPreview, setLinkPreview] = useState<ExternalLinkPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const primaryLink = getPrimaryExternalUrl(content);
  const isPostTooLong = !isKinfeedPostWithinLimit(content);
  const canPost = Boolean(content.trim() || sharePayload || photos.length || videoPath) && !isPostTooLong;

  useEffect(() => {
    let active = true;
    if (!primaryLink) {
      setLinkPreview(null);
      setPreviewLoading(false);
      return () => { active = false; };
    }

    setPreviewLoading(true);
    const timeout = window.setTimeout(() => {
      void fetchExternalLinkPreview(primaryLink).then((preview) => {
        if (!active) return;
        setLinkPreview(preview);
        setPreviewLoading(false);
      });
    }, 350);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [primaryLink]);

  useEffect(() => () => {
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
  }, [videoPreviewUrl]);

  const toggleTopicTag = (tagId: KinfeedTopicTagId) => {
    setTopicTags((current) => {
      if (current.includes(tagId)) return current.filter((value) => value !== tagId);
      if (current.length >= KINFEED_MAX_TOPIC_TAGS) return [...current.slice(1), tagId];
      return [...current, tagId];
    });
  };

  const uploadVideo = async (file: File) => {
    if (!user?.id) return;
    if (!ALLOWED_VIDEO_TYPES.has(file.type)) {
      toast.error("Choose an MP4, MOV or WebM video");
      return;
    }
    if (file.size > MAX_VIDEO_BYTES) {
      toast.error("Videos must be smaller than 100 MB");
      return;
    }

    setVideoUploading(true);
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
    const path = `${user.id}/${Date.now()}-${crypto.randomUUID()}-${safeName}`;
    const { error } = await supabase.storage.from("community-media").upload(path, file, {
      contentType: file.type,
      upsert: false,
    });
    setVideoUploading(false);

    if (error) {
      toast.error("Couldn't upload this video");
      return;
    }

    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    setVideoPath(path);
    setVideoPreviewUrl(URL.createObjectURL(file));
  };

  const removeVideo = async () => {
    const path = videoPath;
    setVideoPath(null);
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    setVideoPreviewUrl(null);
    if (path) await supabase.storage.from("community-media").remove([path]);
  };

  const handlePost = async () => {
    if (isPostTooLong) {
      toast.error(`Posts can contain up to ${KINFEED_POST_MAX_LENGTH.toLocaleString("en-GB")} characters`);
      return;
    }
    if (!canPost || !user || posting || videoUploading) return;
    setPosting(true);
    const { data: createdPost, error } = await supabase
      .from("feed_posts" as any)
      .insert({
        user_id: user.id,
        content: composeKindredContent(content, sharePayload),
        photo_urls: photos,
        video_urls: videoPath ? [videoPath] : [],
        link_preview: linkPreview,
        topic_tags: topicTags,
      } as any)
      .select("id")
      .single();
    setPosting(false);

    if (error) {
      toast.error("Couldn't create post");
      return;
    }

    if ((createdPost as any)?.id) {
      await dispatchMentionNotifications({
        actorId: user.id,
        actorDisplayName: user.user_metadata?.display_name ?? user.email?.split("@")[0] ?? "Someone",
        content,
        mentions,
        bodyContext: "in Kinfeed",
        entityType: "post",
        entityId: (createdPost as any).id,
        url: `/community/feed/${(createdPost as any).id}`,
      });
    }

    toast.success("Posted to Kinfeed");
    navigate("/kinfeed");
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="kindred-screen-header kindred-screen-header--between">
        <button type="button" aria-label="Back" onClick={() => navigate(-1)} className="text-secondary-foreground">
          <ArrowLeft size={22} />
        </button>
        <h1 className="kindred-screen-title">New post</h1>
        <AppButton size="sm" onPress={() => void handlePost()} disabled={!canPost || posting || videoUploading}>
          {posting ? "Posting..." : "Post"}
        </AppButton>
      </header>

      <div className="flex-1 p-4">
        <AppCard>
          <AppCardBody gap="$md">
            {sharePayload ? (
              <div>
                <AppText className="mb-2 text-xs font-medium uppercase text-muted-foreground">Sharing into Kinfeed</AppText>
                <KindredShareCard payload={sharePayload} onOpen={() => navigate(sharePayload.route)} className="mt-0" />
              </div>
            ) : null}

            <MentionComposerField
              label="Your post"
              value={content}
              onChangeText={setContent}
              mentions={mentions}
              onMentionsChange={setMentions}
              lookupCandidates={(query) => searchAllMentionSuggestions(query, user?.id)}
              placeholder={sharePayload ? "Add a note (optional)..." : "What's on your mind?"}
              multiline
              autoFocus
              helper={isPostTooLong ? "This post is too long to publish." : "Type @ to tag another member."}
              inputProps={{ minHeight: 150, maxLength: KINFEED_POST_MAX_LENGTH }}
            />
            <AppText className={`text-right text-xs ${isPostTooLong ? "text-destructive" : "text-muted-foreground"}`}>
              {content.length.toLocaleString("en-GB")} / {KINFEED_POST_MAX_LENGTH.toLocaleString("en-GB")}
            </AppText>

            {primaryLink ? (
              <div className="rounded-[16px] border border-border/70 bg-muted/30 p-2">
                {previewLoading ? (
                  <div className="flex min-h-16 items-center justify-center gap-2 text-sm text-muted-foreground">
                    <LoaderCircle size={16} className="animate-spin" />
                    Loading link preview...
                  </div>
                ) : (
                  <ExternalLinkPreviewCard url={primaryLink} storedPreview={linkPreview} />
                )}
              </div>
            ) : null}

            <div className="grid grid-cols-3 gap-2 border-y border-border/60 py-2">
              <button
                type="button"
                onClick={() => setPhotoAreaOpen((open) => !open)}
                className={`inline-flex min-h-10 items-center justify-center gap-2 rounded-md px-2 text-sm font-semibold transition-colors hover:bg-muted ${photoAreaOpen || photos.length ? "text-primary" : "text-muted-foreground"}`}
              >
                <ImageIcon size={18} />
                <span>Photo</span>
              </button>
              <button
                type="button"
                onClick={() => videoInputRef.current?.click()}
                disabled={videoUploading || Boolean(videoPath)}
                className={`inline-flex min-h-10 items-center justify-center gap-2 rounded-md px-2 text-sm font-semibold transition-colors hover:bg-muted disabled:opacity-50 ${videoPath ? "text-primary" : "text-muted-foreground"}`}
              >
                {videoUploading ? <LoaderCircle size={18} className="animate-spin" /> : <Video size={18} />}
                <span>Video</span>
              </button>
              <button
                type="button"
                onClick={() => setTopicsOpen((open) => !open)}
                className={`inline-flex min-h-10 items-center justify-center gap-2 rounded-md px-2 text-sm font-semibold transition-colors hover:bg-muted ${topicsOpen || topicTags.length ? "text-primary" : "text-muted-foreground"}`}
              >
                <Tag size={18} />
                <span>Tag{topicTags.length ? ` (${topicTags.length})` : ""}</span>
              </button>
            </div>

            <input
              ref={videoInputRef}
              type="file"
              accept="video/mp4,video/quicktime,video/webm"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void uploadVideo(file);
                event.currentTarget.value = "";
              }}
            />

            {photoAreaOpen || photos.length ? (
              <AppFieldGroup helper="Add up to 4 photos">
                <PhotoUploadButton
                  bucket="community-photos"
                  multiple
                  existingPhotos={photos}
                  onRemoveExisting={(index) => setPhotos((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                  onPhotoReady={() => {}}
                  onClear={() => {}}
                  previewUrl={null}
                  onMultipleReady={(urls) => setPhotos((current) => [...current, ...urls].slice(0, 4))}
                  returnObjectPath
                  buttonLabel={photos.length ? "Add another" : "Choose photos"}
                />
              </AppFieldGroup>
            ) : null}

            {videoPreviewUrl ? (
              <div className="relative overflow-hidden rounded-[16px] bg-black">
                <video src={videoPreviewUrl} controls playsInline preload="metadata" className="aspect-video w-full object-contain" />
                <button type="button" aria-label="Remove video" onClick={() => void removeVideo()} className="absolute right-2 top-2 rounded-full bg-black/70 p-2 text-white hover:bg-black">
                  <X size={16} />
                </button>
              </div>
            ) : null}

            {topicsOpen ? (
              <AppFieldGroup helper={`Optional. Pick up to ${KINFEED_MAX_TOPIC_TAGS} topics.`}>
                <div className="space-y-2">
                  <AppText className="text-sm font-medium text-foreground">Topics</AppText>
                  <div className="flex flex-wrap gap-2">
                    {KINFEED_TOPIC_TAGS.map((tag) => {
                      const selected = topicTags.includes(tag.id);
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => toggleTopicTag(tag.id)}
                          className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${selected
                            ? "border-primary/20 bg-primary/10 text-primary"
                            : "border-border bg-background text-muted-foreground hover:border-primary/20 hover:text-primary"}`}
                        >
                          {tag.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </AppFieldGroup>
            ) : null}

            <AppButtonRow>
              <AppButton flex={1} onPress={() => void handlePost()} disabled={!canPost || posting || videoUploading}>
                {posting ? "Posting..." : "Post to Kinfeed"}
              </AppButton>
              <AppButton flex={1} tone="outline" onPress={() => navigate(-1)}>Cancel</AppButton>
            </AppButtonRow>
          </AppCardBody>
        </AppCard>
      </div>
    </div>
  );
};

export default ComposePost;
